// Escape sequence for dots in original key names
export const ESCAPED_DOT = '___DOT___'

// Escape dots in a key name to distinguish from nesting dots
export const escapeDotsInKey = (key: string): string => {
  return key.replace(/\./g, ESCAPED_DOT)
}

// Unescape dots in a key name to restore original key
export const unescapeDotsInKey = (key: string): string => {
  return key.replace(new RegExp(ESCAPED_DOT, 'g'), '.')
}

// Common traversal function that handles dot escaping and returns all paths
const traversePayloadFormat = (
  obj: Record<string, string | Record<string, unknown>>,
  prefix = '',
  escapeDots = true,
): Array<{ path: string; value: string }> => {
  const results: Array<{ path: string; value: string }> = []

  Object.entries(obj).forEach(([key, value]) => {
    // Escape dots in the actual key name to distinguish from nesting dots (if enabled)
    const processedKey = escapeDots ? escapeDotsInKey(key) : key
    const newKey = prefix ? `${prefix}.${processedKey}` : processedKey

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Recurse for nested objects
      results.push(
        ...traversePayloadFormat(
          value as Record<string, string | Record<string, unknown>>,
          newKey,
          escapeDots,
        ),
      )
    } else {
      // It's a type string - store both path and value
      results.push({ path: newKey, value: value as string })
    }
  })

  return results
}

// Flatten nested objects into dot-notation paths
// Dots in original key names are escaped to distinguish from nesting dots
export const flattenPayloadFormat = (
  obj: Record<string, string | Record<string, unknown>>,
  prefix = '',
): Record<string, string> => {
  const results = traversePayloadFormat(obj, prefix, true)
  return results.reduce(
    (acc, { path, value }) => {
      acc[path] = value
      return acc
    },
    {} as Record<string, string>,
  )
}

// Extract paths from payload format (used for node variables)
// Now uses the same traversal logic with dot escaping for consistency
export const getPayloadPaths = (
  obj: Record<string, string | Record<string, unknown>>,
  prefix = '',
): string[] => {
  const results = traversePayloadFormat(obj, prefix, true)
  return results.map(({ path }) => path)
}
